import { useEffect, useRef } from 'react';
import { isRunActive, useInvalidateRunOutputs, useRuns, useTriggerRun } from './useHaloV2';

interface CoverageBannerProps {
  storeId: number;
  start: string;
  end: string;
  coverage: { firstDate: string; lastDate: string; rowCount: number };
}

function spanDays(start: string, end: string): number {
  return (Date.parse(end + 'T00:00:00Z') - Date.parse(start + 'T00:00:00Z')) / 86_400_000;
}

export function CoverageBanner({ storeId, start, end, coverage }: CoverageBannerProps) {
  const rangeExceedsCoverage = start < coverage.firstDate || end > coverage.lastDate;
  if (!rangeExceedsCoverage) return null;

  return <CoverageBannerInner storeId={storeId} start={start} end={end} coverage={coverage} />;
}

function CoverageBannerInner({ storeId, start, end, coverage }: CoverageBannerProps) {
  const runsQuery = useRuns();
  const trigger = useTriggerRun();
  const invalidateOutputs = useInvalidateRunOutputs();
  const prevActiveIds = useRef<Set<string>>(new Set());

  useEffect(() => {
    const runs = runsQuery.data ?? [];
    const nowActive = new Set(runs.filter(isRunActive).map((r) => r.id));
    const completed = [...prevActiveIds.current].some((id) => !nowActive.has(id));
    prevActiveIds.current = nowActive;
    if (completed) invalidateOutputs();
  }, [runsQuery.data, invalidateOutputs]);

  const hasActiveRun = (runsQuery.data ?? []).some(isRunActive);
  const days = spanDays(start, end);
  const exceedsLimit = days > 400;

  const onBackfill = () => {
    const desc = `Backfill store ${storeId}, ${start} to ${end}?`;
    if (window.confirm(desc)) {
      trigger.mutate({ mode: 'backfill', storeIds: [storeId], startDate: start, endDate: end });
    }
  };

  return (
    <div className="rounded-lg border border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-950/40 px-4 py-2.5 text-sm flex flex-wrap items-center gap-x-4 gap-y-1.5">
      <span className="text-amber-800 dark:text-amber-300">
        Selected range extends beyond computed data (covered{' '}
        <span className="font-medium">{coverage.firstDate}</span>
        {' → '}
        <span className="font-medium">{coverage.lastDate}</span>).
      </span>

      {hasActiveRun ? (
        <span className="text-amber-600 dark:text-amber-400 text-xs">
          backfill running — charts refresh when it completes
        </span>
      ) : exceedsLimit ? (
        <span className="text-amber-600 dark:text-amber-400 text-xs">
          range exceeds the 400-day backfill limit — narrow it
        </span>
      ) : (
        <div className="flex items-center gap-2">
          <button
            type="button"
            disabled={trigger.isPending}
            onClick={onBackfill}
            className="rounded-md border border-amber-400 dark:border-amber-600 bg-amber-100 dark:bg-amber-900/60 px-2.5 py-1 text-xs font-medium text-amber-800 dark:text-amber-200 hover:bg-amber-200 dark:hover:bg-amber-800/60 disabled:opacity-50"
          >
            Backfill {start} &rarr; {end}
          </button>
          {trigger.isError ? (
            <span className="text-xs text-red-600 dark:text-red-400">
              {trigger.error?.message ?? 'Run trigger failed'}
            </span>
          ) : null}
        </div>
      )}
    </div>
  );
}
